import { ReferencesApiTypes, referencesApi } from 'api'
import { useEffect, useState } from 'react'
import { ItemType } from 'ui/atoms/Select/types'
import {
  setCitiesData,
  setCountriesData,
  setLocationsData,
  setStatesData,
} from 'redux/locations/slice'
import { useDispatch } from 'react-redux'
import { useSelectValues } from '../../../hooks/useSelectValues'
import { useApiRequest } from 'hooks/useApiRequest'
import { useCompanyId } from 'hooks/useCompanyId'
import { countriesWithState } from 'constants/locations'
import { TypeOfLocation } from 'api/references/types'

export type Location = {
  [key: string]: {
    name: string
    id: string
    code?: string
  }
}

const EMPTY_ITEM = { name: '', id: '' }

export const useSelectLocation = (
  currentLocation: Location,
  changeLocation: (location: Location) => void,
  locationId?: string,
  pendingLocationId?: string,
  pendingCityId?: string,
  locationType?: TypeOfLocation
) => {
  const dispatch = useDispatch()
  const [countries, setCountries] =
    useState<ReferencesApiTypes.CountriesResponse>({ items: [] })
  const [cities, setCities] = useState<ReferencesApiTypes.CitiesResponse>({
    items: [],
  })
  const [locations, setLocations] =
    useState<ReferencesApiTypes.LocationsResponse>({ items: [] })
  const [states, setStates] = useState<ReferencesApiTypes.LocationEntityType[]>(
    []
  )
  const companyId = useCompanyId()
  const isCountryWithStates = countriesWithState.includes(
    Number(currentLocation.country.id)
  )

  const availableCountriesRequest = useApiRequest(() =>
    referencesApi.getAvailableCountries(locationId)
  )

  const availableCitiesRequest = useApiRequest((countryId) =>
    referencesApi.getAvailableCities(countryId, locationId)
  )

  const availableLocationsRequest = useApiRequest((cityId) =>
    referencesApi.getAvailableLocations({
      cityId,
      companyId: String(companyId),
      currentLocationId: locationId,
      pendingLocationId,
      pendingCityId,
      typeOfLocation: locationType,
    })
  )

  const handleChangeLocation = (type: string, value: ItemType) => {
    changeLocation({
      ...currentLocation,
      [type]: { name: value.label, id: value.value },
    })
  }

  useEffect(() => {
    const fetchCountries = async () => {
      const fetchCountriesResponse =
        await availableCountriesRequest.apiRequest()
      if (fetchCountriesResponse) {
        setCountries(fetchCountriesResponse.data)
      }
    }
    fetchCountries()
  }, [companyId])

  const fetchCities = async (countryId: number) => {
    const fetchCitiesResponse = await availableCitiesRequest.apiRequest(
      countryId
    )
    if (fetchCitiesResponse) {
      setCities(fetchCitiesResponse.data)
    }
  }

  const fetchLocations = async (cityId: number) => {
    if (cityId) {
      const fetchLocationsResponse = await availableLocationsRequest.apiRequest(
        cityId
      )
      if (fetchLocationsResponse) {
        setLocations(fetchLocationsResponse.data)
      }
    } else {
      setLocations({ items: [] })
    }
  }

  useEffect(() => {
    if (currentLocation.country.id !== '' && isCountryWithStates) {
      setStates(
        countries.items.find(
          (country) => Number(country.id) === Number(currentLocation.country.id)
        )?.meta.States || []
      )
    }
  }, [currentLocation.country.id, countries])

  useEffect(() => {
    if (currentLocation.country.id !== '' && !cities.items.length) {
      if (isCountryWithStates && currentLocation.state.id) {
        fetchCities(Number(currentLocation.state.id))
      } else {
        fetchCities(Number(currentLocation.country.id))
      }
    }

    if (currentLocation.city.id && locationType) {
      fetchLocations(Number(currentLocation.city.id))
    }
  }, [
    currentLocation.country.id,
    currentLocation.city.id,
    currentLocation.state.id,
    locationType,
  ])

  const handleCountryChange = (country: ItemType) => {
    if (currentLocation.country.id !== country.value) {
      setCities({
        items: [],
      })
      setStates([])
      setLocations({
        items: [],
      })
    }
    changeLocation({
      country: { name: country.label, id: country.value },
      state: EMPTY_ITEM,
      city: EMPTY_ITEM,
      location: EMPTY_ITEM,
    })
  }

  const handleStateChange = (state: ItemType) => {
    if (currentLocation.state.id && currentLocation.state.id !== state.value) {
      setCities({
        items: [],
      })

      setLocations({
        items: [],
      })
    }
    changeLocation({
      ...currentLocation,
      state: { name: state.label, id: state.value },
      city: EMPTY_ITEM,
      location: EMPTY_ITEM,
    })
  }

  const handleCityChange = (city: ItemType) => {
    if (currentLocation.city.id !== city.value) {
      setLocations({
        items: [],
      })
    }
    handleChangeLocation('city', city)
  }

  const handleLocationChange = (location: ItemType) => {
    handleChangeLocation('location', location)
  }

  const countriesSelectValues = useSelectValues(countries.items)
  const citiesSelectValues = useSelectValues(cities.items)
  const locationsSelectValues = useSelectValues(locations.items)
  const statesSelectValues = useSelectValues(states)

  useEffect(() => {
    dispatch(setCitiesData(citiesSelectValues))
    dispatch(setCountriesData(countriesSelectValues))
    dispatch(setLocationsData(locationsSelectValues))
    dispatch(setStatesData(statesSelectValues))
  }, [
    citiesSelectValues,
    countriesSelectValues,
    locationsSelectValues,
    statesSelectValues,
  ])

  return {
    handleCountryChange,
    handleCityChange,
    handleLocationChange,
    handleStateChange,
    countriesSelectValues,
    citiesSelectValues,
    locationsSelectValues,
    statesSelectValues,
    countries,
    locations,
    cities,
    loading: {
      countries: availableCountriesRequest.loading,
      cities: availableCitiesRequest.loading,
      locations: availableLocationsRequest.loading,
    },
    isLoaded:
      availableCountriesRequest.isLoaded &&
      availableCitiesRequest.isLoaded &&
      availableLocationsRequest.isLoaded,
  }
}
